import { FormInstance } from "antd";
import { Ref } from "react";
import { styled } from "styled-components";
import {
  Award as AwardModel,
  FilmProfile,
} from "../../../models/film-profile.model";
import { SetState, StatePair } from "../../../types/SetState";
import { Subject } from "rxjs";
import { StepOne } from "./steps/step-one.step";
import { StepTwo } from "./steps/step-two.step";
import { StepThree } from "./steps/step-three.step";
import { StepFour } from "./steps/step-four.step";
import { StepFive } from "./steps/step-five.step";
import { StepSix } from "./steps/step-six.step";
import { ReviewStep } from "./steps/review.step";

const StepZeroGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.6rem;
  width: 100%;
  align-items: stretch;

  @media (max-width: 64em) {
    grid-template-columns: 1fr;
  }
`;

const StepZeroFullRow = styled.div`
  grid-column: 1 / -1;
`;



export const Step = ({
  step,
  loaded,
  awardForm,
  setCurrentStep,
  setIsShowingAwardModal,
  verticalPoster,
  setVerticalPoster,
  horizontalPoster,
  setHorizontalPoster,
  productionStills,
  setProductionStills,
  directors,
  setDirectors,
  producers,
  setProducers,
  cast,
  setCast,
  crew,
  setCrew,
  setAwardEditingIndex,
  awards,
  setAwards,
  nameOptions,
  professionalProfileSubject$,
  form,
  onFilmTitleChange,
  titleFieldsAutoFocus,
  completedSteps,
  setNameModalType,
  synopsis,
  setSynopsis,
  directorsStatement,
  setDirectorsStatement,
  producersStatement,
  setProducersStatement,
  otherInformation,
  setOtherInformation,
  keywords,
  setKeywords,
  posterErrors,
  setPosterErrors,
  customUrlInputRef,
  customUrlSubject$,
  customUrl,
  urlVerified,
  showVerification,
}: {
  form: FormInstance<FilmProfile>;
  step: number;
  awardForm: FormInstance<AwardModel>;
  setIsShowingAwardModal: SetState<boolean>;
  setAwardEditingIndex: SetState<number | null>;
  setNameModalIndex: SetState<number>;
  nameOptions: {
    value: string;
    label: string;
    logo?: string;
    id?: string;
    full_name?: string;
    custom_url?: string;
    type?: "professional" | "business";
  }[];
  nameModalIndex: number;
  professionalProfileSubject$: Subject<string>;
  onFilmTitleChange: (title: string) => void;
  titleFieldsAutoFocus?: boolean;
  completedSteps: (boolean | null)[];
  setCurrentStep: SetState<number>;
  loaded: boolean;
  customUrlInputRef: Ref<HTMLInputElement>;
  customUrlSubject$: Subject<string>;
  customUrl: string;
  urlVerified: boolean | null;
  showVerification: boolean;
} & StatePair<"directorsStatement", "setDirectorsStatement", string> &
  StatePair<"producersStatement", "setProducersStatement", string> &
  StatePair<"otherInformation", "setOtherInformation", string> &
  StatePair<"synopsis", "setSynopsis", string> &
  StatePair<
    "verticalPoster",
    "setVerticalPoster",
    {
      url: string | null;
      file: File | null;
    }[]
  > &
  StatePair<"searchQuery", "setSearchQuery", string> &
  StatePair<
    "horizontalPoster",
    "setHorizontalPoster",
    {
      url: string | null;
      file: File | null;
    }[]
  > &
  StatePair<
    "productionStills",
    "setProductionStills",
    {
      url: string | null;
      file: File | null;
    }[]
  > &
  StatePair<"isDistributionChecked", "setIsDistributionChecked", boolean> &
  StatePair<"isSubtitlesAvailable", "setIsSubtitlesAvailable", boolean> &
  StatePair<
    "directors",
    "setDirectors",
    {
      name: string;
      user?: string;
      biography: string;
      id?: string;
      type?: "professional" | "business";
      logo?: string;
    }[]
  > &
  StatePair<
    "producers",
    "setProducers",
    {
      name: string;
      user?: string;
      filmography: string;
      id?: string;
      type?: "professional" | "business";
      logo?: string;
    }[]
  > &
  StatePair<
    "cast",
    "setCast",
    {
      name: string;
      user?: string;
      character: string;
      id?: string;
      type?: "professional" | "business";
      logo?: string;
    }[]
  > &
  StatePair<
    "crew",
    "setCrew",
    {
      name: string;
      user?: string;
      role: string;
      id?: string;
      type?: "professional" | "business";
      logo?: string;
    }[]
  > &
  StatePair<"awards", "setAwards", AwardModel[]> &
  StatePair<"subtitles", "setSubtitles", { language: string; file: string }[]> &
  StatePair<"nameModalType", "setNameModalType", 0 | 1 | 2 | 3 | null> &
  StatePair<"keywords", "setKeywords", string[]> &
  StatePair<
    "posterErrors",
    "setPosterErrors",
    [boolean, boolean, boolean]
  >) => {
  return (
    <>
      {step === 0 ? (
        <StepZeroGrid>
          <StepOne
            {...{
              onFilmTitleChange,
              titleFieldsAutoFocus,
              form,
              customUrlInputRef,
              customUrlSubject$,
              customUrl,
              urlVerified,
              showVerification,
            }}
            visible
            fillHeight
          />
          <StepTwo
            {...{ keywords, setKeywords, synopsis, setSynopsis }}
            visible
            fillHeight
          />
          <StepZeroFullRow>
            <StepThree visible />
          </StepZeroFullRow>
        </StepZeroGrid>
      ) : (
        <>
          <StepOne
            {...{
              onFilmTitleChange,
              titleFieldsAutoFocus,
              form,
              customUrlInputRef,
              customUrlSubject$,
              customUrl,
              urlVerified,
              showVerification,
            }}
            visible={false}
          />
          <StepTwo
            {...{ keywords, setKeywords, synopsis, setSynopsis }}
            visible={false}
          />
          <StepThree visible={false} />
        </>
      )}
      <StepFour
        {...{
          directors,
          setDirectors,
          producers,
          setProducers,
          cast,
          setCast,
          crew,
          setCrew,
          nameOptions,
          professionalProfileSubject$,
          setNameModalType,
        }}
        visible={step === 1}
      />
      <StepFive
        {...{
          directors,
          producers,
          cast,
          crew,
          setAwardEditingIndex,
          setAwards,
          awards,
          setIsShowingAwardModal,
          awardForm,
        }}
        visible={step === 2}
      />
      <StepSix
        {...{
          verticalPoster,
          setVerticalPoster,
          horizontalPoster,
          setHorizontalPoster,
          productionStills,
          setProductionStills,
          directorsStatement,
          setDirectorsStatement,
          producersStatement,
          setProducersStatement,
          otherInformation,
          setOtherInformation,
          form,
          posterErrors,
        }}
        visible={step === 3}
      />
      {step === 4 ? (
        <ReviewStep
          {...{
            form,
            directors,
            cast,
            crew,
            producers,
            awards,
            verticalPoster,
            horizontalPoster,
            productionStills,
            completedSteps,
            setCurrentStep,
            synopsis,
            setPosterErrors,
            keywords,
            loaded,
          }}
        />
      ) : null}
    </>
  );
};
